import { CLEAR_RESULT, EXECUTE_DEPOSIT, PROMPT_KYC, PROMPT_SECUREID } from '@comeon/actions-public';
import Cookies from '@comeon/tomcat-cookies';

import { updateCC } from '~/components/payment/deposit-beta/pages/deposit-amount/ducks';
import matchResponseFilter from '~/components/payment/deposit/data/deposit-response-filter';
import {
	cancelPrompts,
	getCreditCardEncryptionKey,
	isKycNeeded,
	updateExecutePrecheck,
	validateTemplate,
	wrappedRequestApi
} from '~/components/payment/ducks';
import { getCVVBypass, hydratePaymentMethods } from '~/components/payment/ducks/payment';
import encryptStringWithRsaPublicKey from '~/components/payment/util/encrypt-util';
import { FREEBET, OPT_IN } from '~/constants';

const NO_BONUS_ID_TYPES = [FREEBET, OPT_IN];

async function getKYCStatus(getState) {
	const {
		spec,
		status: { promptsecureid, schufa_consent_form: showSchufaConsent = false },
		feesAndLimits: { paymentProviderData }
	} = getState().payment;
	const isTemporaryPlayer = getState().account.temporaryPlayer?.temporary;
	const { cid } = spec ?? {};

	if (!isTemporaryPlayer) {
		const kyc = await isKycNeeded(
			paymentProviderData.PARAM_PAYMENT_METHOD_ID,
			paymentProviderData.PARAM_PAYMENT_PROVIDER_ID,
			cid
		);
		const { needValidation } = kyc;
		if (needValidation) {
			return {
				type: PROMPT_KYC,
				payload: kyc
			};
		}
	}

	/* Schufa check should be done only after KYC check, hence we will keep it here */
	if (showSchufaConsent && promptsecureid !== 'showShufaConsent') {
		return {
			type: PROMPT_SECUREID,
			payload: 'showShufaConsent'
		};
	}
	return false;
}

function updateExecuteDepositResponse(response) {
	const failure = response && response.successful === false;

	const errorMessage = failure
		? {
				error: true,
				title: response.errorMessage.title,
				message: response.errorMessage.message,
				availableDepositAmount:
					typeof response.availableDepositAmount !== 'undefined' && response.availableDepositAmount !== null
						? response.availableDepositAmount
						: null
			}
		: null;
	const result = failure ? errorMessage : matchResponseFilter(response, 'SEND_HANDLER_CONTROLLER');
	result.readonly = true;
	result.successful = true;
	return dispatch => {
		dispatch({
			type: EXECUTE_DEPOSIT,
			payload: { result }
		});
	};
}
async function encryptCreditCardInformation(spec, dispatch, formTemplateCode, preferredCardProviderData) {
	let { paymentMethodId } = spec;
	let providerId;
	if (preferredCardProviderData) {
		paymentMethodId = preferredCardProviderData.PARAM_PAYMENT_METHOD_ID;
		providerId = preferredCardProviderData.PARAM_PAYMENT_PROVIDER_ID;
	}
	if (
		formTemplateCode &&
		(formTemplateCode === 'CREDIT-CARD' ||
			formTemplateCode === 'DIRECTLY-ROUTED-CREDIT-CARD' ||
			formTemplateCode === 'ASTROPAY-CREDIT-CARD')
	) {
		let publicKey = null;
		await getCreditCardEncryptionKey(dispatch).then(creditCardEncryptResponse => {
			publicKey = creditCardEncryptResponse.result;
		});
		if (publicKey && publicKey !== '') {
			const encryptedCreditCardNumber = await encryptStringWithRsaPublicKey(spec.cn, publicKey);
			const encryptedExpiryMonth = await encryptStringWithRsaPublicKey(spec.vm, publicKey);
			const encryptedExpiryYear = await encryptStringWithRsaPublicKey(spec.vy, publicKey);
			const encryptedCvc = spec.cc ? await encryptStringWithRsaPublicKey(spec.cc, publicKey) : '';

			return {
				...spec,
				cn: encryptedCreditCardNumber,
				vm: encryptedExpiryMonth,
				vy: encryptedExpiryYear,
				cc: encryptedCvc,
				paymentMethodId,
				providerId
			};
		}
		// eslint-disable-next-line no-console
		console.error('error while getting credit card encryption key');
	}
	return {
		...spec,
		paymentMethodId,
		providerId
	};
}

function executeDeposit(addSeonEvent) {
	return async (dispatch, getState) => {
		const state = getState();
		const {
			spec,
			feesAndLimits: { paymentProviderData }
		} = state.payment;
		const { formTemplateCode } = state.payment?.status || {};
		const { paymentMethodId } = state.payment?.spec || {};

		const encryptedSpec = await encryptCreditCardInformation(spec, dispatch, formTemplateCode, paymentProviderData);
		const { executeUrl, sendHandlerType } = state.payment.paymentMethods[paymentMethodId];

		// prevent multiple clicks on deposit submit
		if (!spec || spec.exe_pending) return;

		const { bonusId: depositBonusId, selectedWelcomePackageId } = state.bonuses;
		const { bonusId: shopItemBonusId, type } = state.payment.shopItem;
		const bonusId = NO_BONUS_ID_TYPES.includes(type) ? null : shopItemBonusId || depositBonusId;
		// const bid = bonusId || selectedWelcomePackageId === null ? selectedWelcomePackageId : -selectedWelcomePackageId;
		dispatch({
			type: EXECUTE_DEPOSIT,
			payload: {
				result: null,
				spec: {
					...spec,
					bonusId,
					welcomeBonusPackageId: selectedWelcomePackageId,
					exe_pending: true,
					executeUrl
				}
			}
		});
		const params = {};
		/* remove properties that shall not be included in request
			and take care of 'paymentMethodId' and 'methodId' that are
			the same.
			Remve all 'validFor...' properties
			copy paymentMethodId to methodId
			add 'isApplePayMethod = false'
		spec:
		{
			"am": 100,
			"cu": "SEK",
			"utimestamp": null,
			"paymentMethodId": 5,
			"isMobileDevice": true,
			"externalid": "453313818311",
			"id": "173419",
			"validatedSpec": false,
			"validSecureId": true,
			"validAccount": true,
			"validCurrency": true,
			"validForSubmit": true
		} */
		Object.keys(encryptedSpec).forEach(key => {
			if (
				key === 'validatedSpec' ||
				key === 'validSecureId' ||
				key === 'validAccount' ||
				key === 'validCurrency' ||
				key === 'validForSubmit'
			) {
				return;
			}
			if (key === 'am') {
				params.am = encryptedSpec.am.toString();
				return;
			}
			if (key === 'paymentMethodId') {
				params[key] = encryptedSpec.paymentMethodId;
				params.methodId = encryptedSpec.paymentMethodId;
			} else {
				params[key] = encryptedSpec[key];
			}
			if (key === 'playerIdEncrypted') {
				params[key] = encryptedSpec[key];
			}
		});

		params.bonusId = bonusId;
		params.bid = bonusId;
		params.welcomeBonusPackageId = selectedWelcomePackageId;
		params.isApplePayMethod = false;
		params.isMobileDevice = true;
		params.depositType = 2;

		// Attach the bonusAddOnId and addOnOptionId if they are available
		const { shopItem } = state.payment;
		if (shopItem && shopItem.baid) {
			params.baid = shopItem.baid;
		}
		if (shopItem && shopItem.oid) {
			params.oid = shopItem.oid;
		}
		if (addSeonEvent) await addSeonEvent();
		// TODO: Add a try catch block for better error handling
		const response = await wrappedRequestApi(
			{
				endpoint: executeUrl,
				payload: params,
				method: 'POST',
				urlencodedContentType: false,
				netellerHeaders: true
			},
			dispatch
		);
		const failure = response && response.successful === false;
		const errorMessage = failure
			? {
					error: true,
					...(response.errorMessage ? response.errorMessage : {}),
					availableDepositAmount:
						typeof response.availableDepositAmount !== 'undefined' &&
						response.availableDepositAmount !== null
							? response.availableDepositAmount
							: null
				}
			: null;
		const result = failure ? errorMessage : matchResponseFilter(response, sendHandlerType);
		result.readonly = true;
		return dispatch({
			type: EXECUTE_DEPOSIT,
			payload: { result, spec: { exe_pending: false } }
		});
	};
}
function doConfirmationsAndExecuteDeposit(addSeonEvent) {
	// TODO: compare the spec against requirement for the current method.
	// If not valid, then request missing parameters, for example cvc, secureid,
	// agreementcheck.
	// ui-component, or cvc-input.
	return async (dispatch, getState) => {
		// if method is 1 and no Cvc
		// if method is 5 and no secureid
		// if agreement is not confirmed
		// if age is not verified
		try {
			const {
				status: { formTemplateCode, blikCodeRequired, promptsecureid, selectedMethodDetails },
				methodDetails,
				paymentMethods,
				spec,
				feesAndLimits: { paymentProviderData }
			} = getState().payment;

			dispatch(updateExecutePrecheck({ exe_confirm_pending: true }));

			// prevent multiple clicks on deposit submit
			if (spec?.exe_pending) return;

			const {
				validSecureId,
				validPassword,
				validTransactionId,
				validSwishNumber,
				validDocumentId,
				swishMobileFlow
			} = await validateTemplate(formTemplateCode, spec, blikCodeRequired, methodDetails, paymentMethods);
			const { cid, am } = spec;
			const statusModalReminder = Cookies.getJSON('dontShowAgainMagniusModal');
			const selectedMethodPromptCode = selectedMethodDetails?.extraParameters?.promptModalCode;
			if (!validSecureId && formTemplateCode === 'CREDIT-CARD') {
				const { PARAM_PAYMENT_METHOD_ID: paymentMethodId, PARAM_PAYMENT_PROVIDER_ID: providerId } =
					paymentProviderData || {};
				const isCVVBypass = await getCVVBypass(dispatch, cid, am, providerId, paymentMethodId);
				if (isCVVBypass) {
					dispatch(updateCC({ bypassCvv: true }));
				} else {
					return dispatch({ type: PROMPT_SECUREID, payload: formTemplateCode });
				}
			}
			if (
				!validSecureId &&
				(formTemplateCode === 'NETELLER' ||
					formTemplateCode === 'DIRECTLY-ROUTED-CREDIT-CARD' ||
					formTemplateCode === 'ASTROPAY-CREDIT-CARD')
			) {
				return dispatch({
					type: PROMPT_SECUREID,
					payload: formTemplateCode
				});
			}
			if (
				(!validPassword && formTemplateCode === 'ECOBANQ') ||
				(!validPassword && formTemplateCode === 'EZEE_WALLET') ||
				(!validPassword && formTemplateCode === 'VENUSPOINT_DEPOSIT')
			) {
				return dispatch({
					type: PROMPT_SECUREID,
					payload: formTemplateCode
				});
			}
			if (
				(formTemplateCode === 'BANK_WIRE_INDIA' || formTemplateCode === 'BANK_WIRE_INDIA_UPI') &&
				!(validDocumentId || validTransactionId)
			) {
				return dispatch({
					type: PROMPT_SECUREID,
					payload: formTemplateCode
				});
			}
			if (formTemplateCode === 'SWISH' && !validSwishNumber && !swishMobileFlow) {
				return dispatch({
					type: PROMPT_SECUREID,
					payload: formTemplateCode
				});
			}
			if (
				selectedMethodPromptCode === 'MAGNIUS' &&
				promptsecureid !== selectedMethodPromptCode &&
				!statusModalReminder
			) {
				return dispatch({
					type: PROMPT_SECUREID,
					payload: selectedMethodPromptCode
				});
			}
			if (selectedMethodPromptCode && !promptsecureid) {
				// set selectedMethodPromptCode to prompt only when promptsecureid is null
				return dispatch({
					type: PROMPT_SECUREID,
					payload: selectedMethodPromptCode
				});
			}

			const showKYCprompt = await getKYCStatus(getState);

			if (showKYCprompt) {
				dispatch(cancelPrompts());
				dispatch(updateExecutePrecheck({ exe_confirm_pending: false }));
				return dispatch(showKYCprompt);
			}

			/* All pre checks are done to execute the request */
			dispatch(cancelPrompts());
			dispatch(updateExecutePrecheck({ exe_confirm_pending: false }));

			return dispatch(executeDeposit(addSeonEvent));
		} catch (e) {
			console.error(e); /* eslint-disable-line no-console */
		}
	};
}
function clearResult() {
	return dispatch => {
		dispatch({
			type: CLEAR_RESULT,
			payload: {
				status: {},
				spec: {}
			}
		});
		return dispatch(hydratePaymentMethods());
	};
}

export { clearResult, doConfirmationsAndExecuteDeposit, updateExecuteDepositResponse };
